<template>
  <div class="row-actions" ref="root">
    <button
      ref="btnRef"
      type="button"
      class="btn-icon--opcoes show-on-hover"
      :title="title"
      :disabled="disabled"
      @click.stop="toggle"
    ></button>

    <teleport to="body">
      <div
        v-if="isOpen"
        class="menu-popover"
        :style="popoverStyle"
        role="menu"
        @click.stop
      >
        <div v-if="header" class="menu-header">
          {{ header }}
        </div>

        <button
          v-for="(a, idx) in visibleActions"
          :key="a.key ?? idx"
          type="button"
          class="menu-item"
          :class="[
            a.variant === 'danger' ? 'danger' : '',
            a.disabled ? 'is-disabled' : ''
          ]"
          role="menuitem"
          :disabled="!!a.disabled"
          @click="handleAction(a)"
        >
          <span v-if="a.icon" class="menu-icon">{{ a.icon }}</span>
          <span class="menu-label">{{ a.label }}</span>
        </button>

        <div v-if="!visibleActions.length" class="menu-empty">
          Nenhuma ação disponível
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';

const props = defineProps({
  item: { type: Object, default: null },

  // actions: [{ key, label, icon, variant:'danger', visible, disabled }]
  actions: { type: Array, default: () => [] },

  header: { type: String, default: '' },
  title: { type: String, default: 'Ações' },
  disabled: { type: Boolean, default: false },

  offsetX: { type: Number, default: 0 },
  offsetY: { type: Number, default: 6 },
  minWidth: { type: Number, default: 180 }
});

const emit = defineEmits(['action', 'open', 'close']);

const root = ref(null);
const btnRef = ref(null);

const isOpen = ref(false);
const popoverStyle = ref({
  left: '0px',
  top: '0px',
  minWidth: `${props.minWidth}px`
});

const visibleActions = computed(() =>
  (props.actions || []).filter(a => a && a.visible !== false)
);

function close() {
  if (!isOpen.value) return;
  isOpen.value = false;
  emit('close', props.item);

  document.removeEventListener('click', onDocClick, true);
  document.removeEventListener('keydown', onKeyDown, true);
  window.removeEventListener('resize', computePosition, true);
  window.removeEventListener('scroll', computePosition, true);
}

function onKeyDown(e) {
  if (e.key === 'Escape') close();
}

function onDocClick(e) {
  // clicou fora do root? fecha
  if (!root.value) return;
  if (!root.value.contains(e.target)) close();
}

function computePosition() {
  const btn = btnRef.value;
  if (!btn) return;

  const rect = btn.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const minWidth = props.minWidth;

  let left = rect.right - minWidth + props.offsetX;
  let top = rect.bottom + props.offsetY;

  // clamp horizontal
  left = Math.max(8, Math.min(left, vw - minWidth - 8));

  // se estourar embaixo, abre pra cima
  const estimatedHeight = Math.min(
    visibleActions.value.length * 40 + (props.header ? 36 : 0) + 16,
    320
  );

  if (top + estimatedHeight > vh - 8) {
    top = rect.top - estimatedHeight - props.offsetY;
    top = Math.max(8, top);
  }

  popoverStyle.value = {
    left: `${Math.round(left)}px`,
    top: `${Math.round(top)}px`,
    minWidth: `${minWidth}px`
  };
}

async function open() {
  if (props.disabled) return;

  isOpen.value = true;
  emit('open', props.item);

  await nextTick();
  computePosition();

  document.addEventListener('click', onDocClick, true);
  document.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('resize', computePosition, true);
  window.addEventListener('scroll', computePosition, true);
}

function toggle() {
  if (isOpen.value) close();
  else open();
}

function handleAction(a) {
  if (a.disabled) return;
  emit('action', { key: a.key, item: props.item, action: a });
  close();
}

onBeforeUnmount(() => {
  // garante limpeza se o componente for destruído aberto
  close();
});
</script>

<style scoped>
.row-actions {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.menu-popover {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.14);
  padding: 6px;
  max-height: 320px;
  overflow: auto;
}

.menu-header {
  font-size: 12px;
  padding: 8px 10px;
  color: #666;
  border-bottom: 1px solid #eee;
  margin-bottom: 6px;
}

.menu-item {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 10px 10px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  color: #2f835d;
}

.menu-item:hover {
  background: #daf1e7;
  color: #0e251b;
}

.menu-item.danger {
  color: #b71c1c;
}

.menu-item.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.menu-icon {
  width: 18px;
  display: inline-flex;
  justify-content: center;
}

.menu-label {
  font-size: 14px;
}

.menu-empty {
  padding: 10px;
  color: #777;
  font-size: 13px;
}

/* mantém o comportamento "aparece só no hover da linha" */
.show-on-hover {
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease;
}
tr:hover .show-on-hover {
  opacity: 1;
  pointer-events: auto;
}
</style>
